import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'

import type { UpdateOrganizationRequest } from '../api/organizations'
import {
  getOperatorRestaurant,
  getOperatorRestaurantOwner,
  getOperatorSupplier,
  getOperatorSupplierOwner,
  ORGANIZATION_PLAN_OPTIONS,
  setOperatorRestaurantActive,
  setOperatorRestaurantPlan,
  setOperatorRestaurantVenueSlots,
  setOperatorSupplierActive,
  setOperatorSupplierPlan,
  updateOperatorRestaurant,
  updateOperatorSupplier,
  type OperatorBuyerOwnerDto,
  type OrganizationPlan,
} from '../api/operator'
import type { OrganizationDto, OrganizationType } from '../api/types'
import { listCities, listHorecaTypes, listProductTypesL0 } from '../api/reference'
import { ApiError } from '../api/client'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { SelectCombobox } from '../components/SelectCombobox'
import { ogrnValidationMessage } from '../utils/ogrn'
import {
  operatorBuyerPricesPath,
  operatorRestaurantRfqsPath,
  operatorRestaurantsPath,
  operatorSupplierCatalogPath,
  operatorSuppliersPath,
} from '../operator/paths'

const formatOwnerDate = (iso: string | null): string => {
  if (!iso) {
    return '—'
  }
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ru-RU')
}

type OperatorOrganizationScreenProps = {
  organizationId: number
  orgType: OrganizationType
  backTo: string
  backLabel: string
  actions?: ReactNode
}

export const OperatorOrganizationScreen = ({
  organizationId,
  orgType,
  backTo,
  backLabel,
  actions,
}: OperatorOrganizationScreenProps) => {
  const [org, setOrg] = useState<OrganizationDto | null>(null)
  const [legalName, setLegalName] = useState('')
  const [tradeName, setTradeName] = useState('')
  const [ogrn, setOgrn] = useState('')
  const [city, setCity] = useState('moscow')
  const [horecaType, setHorecaType] = useState('')
  const [productTypeIds, setProductTypeIds] = useState<number[]>([])
  const [cities, setCities] = useState<{ code: string; label: string }[]>([])
  const [horecaTypes, setHorecaTypes] = useState<{ code: string; label: string }[]>([])
  const [supplierTypes, setSupplierTypes] = useState<{ id: number; name: string }[]>([])
  const [plan, setPlan] = useState<OrganizationPlan>('start')
  const [paidVenueSlots, setPaidVenueSlots] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingPlan, setSavingPlan] = useState(false)
  const [togglingActive, setTogglingActive] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [owner, setOwner] = useState<OperatorBuyerOwnerDto | null>(null)
  const [ownerError, setOwnerError] = useState<string | null>(null)

  const displayName = org?.trade_name ?? org?.legal_name ?? `Организация #${organizationId}`

  useEffect(() => {
    void Promise.all([
      listCities().catch(() => []),
      listHorecaTypes().catch(() => []),
      listProductTypesL0().catch(() => []),
    ]).then(([cityOptions, horecaOptions, typeOptions]) => {
      setCities(cityOptions)
      setHorecaTypes(horecaOptions)
      setSupplierTypes(typeOptions)
    })
  }, [])

  useEffect(() => {
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const loaded =
          orgType === 'supplier'
            ? await getOperatorSupplier(organizationId)
            : await getOperatorRestaurant(organizationId)
        setOrg(loaded)
        setLegalName(loaded.legal_name)
        setTradeName(loaded.trade_name ?? '')
        setOgrn(loaded.ogrn ?? '')
        setCity(loaded.city ?? 'moscow')
        setHorecaType(loaded.horeca_type ?? '')
        setProductTypeIds(loaded.product_type_ids ?? [])
        setPlan((loaded.plan as OrganizationPlan | undefined) ?? 'start')
        setPaidVenueSlots(loaded.paid_venue_slots ?? 0)
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить организацию')
      } finally {
        setLoading(false)
      }
    })()
  }, [organizationId, orgType])

  useEffect(() => {
    setOwner(null)
    setOwnerError(null)
    const loadOwner =
      orgType === 'supplier'
        ? getOperatorSupplierOwner(organizationId)
        : getOperatorRestaurantOwner(organizationId)
    void loadOwner
      .then(setOwner)
      .catch((ownerLoadError) =>
        setOwnerError(
          ownerLoadError instanceof Error ? ownerLoadError.message : 'Не удалось загрузить контакты',
        ),
      )
  }, [organizationId, orgType])

  const buildRequest = (): UpdateOrganizationRequest => ({
    legalName: legalName.trim(),
    tradeName: tradeName.trim(),
    ogrn: ogrn.trim(),
    city,
    horecaType: orgType === 'buyer' ? horecaType || null : null,
    productTypeIds: orgType === 'supplier' ? productTypeIds : undefined,
  })

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const ogrnError = ogrnValidationMessage(ogrn)
    if (ogrnError) {
      setMessage(null)
      setError(ogrnError)
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const updated =
        orgType === 'supplier'
          ? await updateOperatorSupplier(organizationId, buildRequest())
          : await updateOperatorRestaurant(organizationId, buildRequest())
      setOrg(updated)
      setMessage('Данные организации сохранены')
    } catch (saveError) {
      setError(saveError instanceof ApiError ? saveError.message : 'Не удалось сохранить')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async () => {
    if (!org) {
      return
    }
    setTogglingActive(true)
    setError(null)
    setMessage(null)
    try {
      const nextActive = !(org.active ?? true)
      const updated =
        orgType === 'supplier'
          ? await setOperatorSupplierActive(organizationId, nextActive)
          : await setOperatorRestaurantActive(organizationId, nextActive)
      setOrg(updated)
      setMessage(nextActive ? 'Организация активирована' : 'Организация деактивирована')
    } catch (toggleError) {
      setError(toggleError instanceof ApiError ? toggleError.message : 'Не удалось изменить статус')
    } finally {
      setTogglingActive(false)
    }
  }

  const handleSavePlan = async () => {
    setSavingPlan(true)
    setError(null)
    setMessage(null)
    try {
      let updated =
        orgType === 'supplier'
          ? await setOperatorSupplierPlan(organizationId, plan)
          : await setOperatorRestaurantPlan(organizationId, plan)
      if (orgType === 'buyer') {
        updated = await setOperatorRestaurantVenueSlots(organizationId, paidVenueSlots)
      }
      setOrg(updated)
      setPlan((updated.plan as OrganizationPlan | undefined) ?? plan)
      setPaidVenueSlots(updated.paid_venue_slots ?? paidVenueSlots)
      setMessage('Тариф и лимиты сохранены')
    } catch (planError) {
      setError(planError instanceof ApiError ? planError.message : 'Не удалось сохранить тариф')
    } finally {
      setSavingPlan(false)
    }
  }

  const toggleProductType = (typeId: number) => {
    setProductTypeIds((current) =>
      current.includes(typeId) ? current.filter((id) => id !== typeId) : [...current, typeId],
    )
  }

  return (
    <section className="market-page">
      <MarketPageHeader
        title={displayName}
        description={orgType === 'supplier' ? 'Карточка поставщика' : 'Карточка ресторана'}
        actions={
          <>
            <Link to={backTo} className="echat-btn echat-btn--ghost">
              ← {backLabel}
            </Link>
            {actions}
          </>
        }
      />

      {org && !(org.active ?? true) ? (
        <p className="echat-badge echat-badge--warning" role="status">
          Организация деактивирована
        </p>
      ) : null}

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {message ? <p className="echat-muted">{message}</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && org ? (
        <div className="market-settings-grid">
          <div className="operator-card-stack">
            <form className="echat-panel market-form" onSubmit={(event) => void handleSubmit(event)}>
            <h2 className="echat-panel__subtitle">Данные организации</h2>

            <div className="market-settings-fields-row">
              <label className="echat-field">
                <span className="echat-field__label">Юридическое название</span>
                <input
                  className="echat-field__input"
                  required
                  value={legalName}
                  onChange={(event) => setLegalName(event.target.value)}
                />
              </label>
              <label className="echat-field">
                <span className="echat-field__label">Торговое название</span>
                <input
                  className="echat-field__input"
                  required
                  value={tradeName}
                  onChange={(event) => setTradeName(event.target.value)}
                />
              </label>
            </div>

            <label className="echat-field">
              <span className="echat-field__label">ОГРН или ОГРНИП</span>
              {/*
                pattern="[0-9]{10}" достался от ИНН и не давал сохранить карточку с ОГРН из 13 цифр
                или ОГРНИП из 15. Номер проверяет ogrnValidationMessage при отправке.
              */}
              <input
                className="echat-field__input"
                required
                inputMode="numeric"
                autoComplete="off"
                placeholder="13 или 15 цифр"
                value={ogrn}
                onChange={(event) => setOgrn(event.target.value)}
              />
            </label>

            {orgType === 'buyer' ? (
              <div className="market-settings-fields-row">
                <SelectCombobox
                  label="Город"
                  value={city}
                  options={cities.map((option) => ({ value: option.code, label: option.label }))}
                  onChange={setCity}
                />
                <SelectCombobox
                  label="Тип заведения"
                  value={horecaType}
                  options={[
                    { value: '', label: '—' },
                    ...horecaTypes.map((option) => ({ value: option.code, label: option.label })),
                  ]}
                  onChange={setHorecaType}
                />
              </div>
            ) : (
              <>
                <SelectCombobox
                  label="Город"
                  value={city}
                  options={cities.map((option) => ({ value: option.code, label: option.label }))}
                  onChange={setCity}
                />
                {supplierTypes.length > 0 ? (
                  <fieldset className="market-fieldset">
                    <legend className="echat-field__label">Категории товаров</legend>
                    <div className="market-checkbox-grid">
                      {supplierTypes.map((type) => (
                        <label key={type.id} className="market-filters__checkbox">
                          <input
                            type="checkbox"
                            checked={productTypeIds.includes(type.id)}
                            onChange={() => toggleProductType(type.id)}
                          />
                          <span>{type.name}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ) : null}
              </>
            )}

            <div className="market-inline-actions">
              <button type="submit" className="echat-btn echat-btn--primary" disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                disabled={togglingActive}
                onClick={() => void handleToggleActive()}
              >
                {togglingActive
                  ? 'Обновление…'
                  : org.active ?? true
                    ? 'Деактивировать'
                    : 'Активировать'}
              </button>
            </div>
            </form>

            {orgType === 'buyer' ? (
              <section className="echat-panel market-form">
                <h2 className="echat-panel__subtitle">Тариф и лимиты</h2>
                <SelectCombobox
                  label="Тариф"
                  value={plan}
                  options={ORGANIZATION_PLAN_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  onChange={(value) => setPlan(value as OrganizationPlan)}
                />
                <label className="echat-field">
                  <span className="echat-field__label">Платные слоты заведений</span>
                  <input
                    className="echat-field__input"
                    type="number"
                    min={0}
                    value={paidVenueSlots}
                    onChange={(event) =>
                      setPaidVenueSlots(Math.max(0, Number(event.target.value) || 0))
                    }
                  />
                </label>
                <div className="market-inline-actions">
                  <button
                    type="button"
                    className="echat-btn echat-btn--primary"
                    disabled={savingPlan}
                    onClick={() => void handleSavePlan()}
                  >
                    {savingPlan ? 'Сохранение…' : 'Сохранить тариф'}
                  </button>
                  {org.plan_display_name ? (
                    <span className="echat-muted">Текущий: {org.plan_display_name}</span>
                  ) : null}
                </div>
              </section>
            ) : null}
          </div>

          <aside className="echat-panel operator-owner-card">
              <h2 className="echat-panel__subtitle">Контакты владельца</h2>
              {ownerError ? (
                <p className="echat-error" role="alert">
                  {ownerError}
                </p>
              ) : !owner ? (
                <p className="echat-muted">Загрузка…</p>
              ) : (
                <dl className="operator-owner-card__list">
                  <div className="operator-owner-card__row">
                    <dt>Корпоративная почта</dt>
                    <dd>{owner.email ? <a href={`mailto:${owner.email}`}>{owner.email}</a> : '—'}</dd>
                  </div>
                  <div className="operator-owner-card__row">
                    <dt>Должность</dt>
                    <dd>{owner.job_title || '—'}</dd>
                  </div>
                  <div className="operator-owner-card__row">
                    <dt>Роль</dt>
                    <dd>{owner.member_role === 'owner' ? 'Владелец' : owner.member_role || '—'}</dd>
                  </div>
                  <div className="operator-owner-card__row">
                    <dt>Регистрация</dt>
                    <dd>{formatOwnerDate(owner.registered_at)}</dd>
                  </div>
                </dl>
              )}
            </aside>
        </div>
      ) : null}
    </section>
  )
}

export const OperatorSupplierOrganizationScreen = () => {
  const { organizationId: organizationIdParam } = useParams()
  const organizationId = Number(organizationIdParam)
  return (
    <OperatorOrganizationScreen
      organizationId={organizationId}
      orgType="supplier"
      backTo={operatorSuppliersPath}
      backLabel="Поставщики"
      actions={
        <Link to={operatorSupplierCatalogPath(organizationId)} className="echat-btn echat-btn--primary">
          Каталог
        </Link>
      }
    />
  )
}

export const OperatorRestaurantOrganizationScreen = () => {
  const { organizationId: organizationIdParam } = useParams()
  const organizationId = Number(organizationIdParam)
  return (
    <OperatorOrganizationScreen
      organizationId={organizationId}
      orgType="buyer"
      backTo={operatorRestaurantsPath}
      backLabel="Рестораны"
      actions={
        <>
          <Link to={operatorBuyerPricesPath(organizationId)} className="echat-btn echat-btn--ghost">
            Мои прайсы
          </Link>
          <Link to={operatorRestaurantRfqsPath(organizationId)} className="echat-btn echat-btn--primary">
            Заявки
          </Link>
        </>
      }
    />
  )
}
